import { useEffect, useState, type ReactNode } from 'react'
import './App.css'
import SeoPage from './SeoPage'
import pages from './generated-pages.json'
import homeContent from './home-content.json'

type ButtonLinkProps = {
  children: ReactNode
  kind?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'lg'
}

const tasks = [
  {
    label: 'Презентация из темы',
    toolbar: 'Презентация продукта',
    eyebrow: 'Новая коллекция',
    title: 'Предметы для спокойного дома',
  },
  {
    label: 'Изображение по описанию',
    toolbar: 'Новый визуал',
    eyebrow: 'Точное описание',
    title: 'Утренний свет в современном интерьере',
  },
  {
    label: 'Обработка загруженного фото',
    toolbar: 'Редактор фотографии',
    eyebrow: 'Готово к публикации',
    title: 'Чистый кадр с нужным настроением',
  },
]

const footerGroups = [
  {
    title: 'Canva',
    links: [
      ['Главная', '/'],
      ['Создать дизайн', '/sozdat-dizajn/'],
      ['Презентации', '/nejroset-dlya-prezentacij/'],
    ],
  },
  {
    title: 'Фото',
    links: [
      ['Редактор фото', '/redaktor-foto/'],
      ['Нейросеть для фото', '/nejroset-dlya-foto/'],
      ['Удалить фон', '/udalit-fon/'],
      ['Заменить фон', '/zamenit-fon-na-foto/'],
      ['Объединить фото', '/obedinit-foto/'],
      ['Оживить фото', '/ozhivit-foto/'],
    ],
  },
  {
    title: 'ИИ',
    links: [
      ['Нейросеть для изображений', '/nejroset-dlya-izobrazhenij/'],
      ['Генератор изображений', '/generator-izobrazhenij/'],
      ['Добавить или удалить объект', '/dobavit-udalit-obekt-na-foto/'],
    ],
  },
]

function usePageMotion() {
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) return

    document.documentElement.classList.add('motion-enabled')

    const copyTargets = document.querySelectorAll<HTMLElement>(
      [
        '.hero-copy',
        '.section-heading',
        '.presentation-copy',
        '.editor-copy',
        '.value-grid > h2',
        '.disclosure-panel > *',
        '.final-copy',
        '.seo-hero-copy',
        '.seo-section-copy',
      ].join(','),
    )

    const visualTargets = document.querySelectorAll<HTMLElement>(
      [
        '.hero-media',
        '.experience-panel',
        '.gallery-media',
        '.presentation-visual',
        '.before-after-media',
        '.editor-panel',
        '.final-orbit',
        '.seo-hero-media',
        '.seo-example-media',
      ].join(','),
    )

    const itemGroups = document.querySelectorAll<HTMLElement>(
      [
        '.capability-grid',
        '.steps-flow',
        '.usecase-grid',
        '.workflow-line',
        '.value-list',
        '.seo-card-grid',
        '.seo-step-list',
        '.seo-benefit-grid',
        '.seo-usecase-list',
        '.seo-audience-list',
        '.related-links',
      ].join(','),
    )

    copyTargets.forEach((target) => target.classList.add('reveal-copy'))
    visualTargets.forEach((target) => target.classList.add('reveal-visual'))
    itemGroups.forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        const target = child as HTMLElement
        target.classList.add('reveal-item')
        target.style.setProperty('--reveal-delay', `${Math.min(index * 55, 220)}ms`)
      })
    })

    const targets = document.querySelectorAll<HTMLElement>(
      '.reveal-copy, .reveal-visual, .reveal-item',
    )
    if (!('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    )

    targets.forEach((target) => observer.observe(target))
    return () => observer.disconnect()
  }, [])
}

function ButtonLink({ children, kind = 'primary', size }: ButtonLinkProps) {
  return (
    <a className={`button button-${kind}${size ? ` button-${size}` : ''}`} href="/go/">
      {children}
    </a>
  )
}

function Header() {
  return (
    <header className="site-header">
      <div className="container header-inner">
        <a className="wordmark" href="/" aria-label="CANVA, на главную">
          CANVA
        </a>
        <nav className="main-nav" aria-label="Основная навигация">
          <a href="#capabilities">Возможности</a>
          <a href="#experience">Как это работает</a>
          <a href="#showcase">Примеры</a>
          <a href="#faq">Вопросы</a>
        </nav>
        <div className="header-actions">
          <ButtonLink size="sm">Попробовать бесплатно</ButtonLink>
        </div>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="hero section" id="top">
      <div className="container hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">Независимый русскоязычный проект</p>
          <h1>Создавайте дизайн без сложных программ</h1>
          <p className="hero-lead">
            Выберите задачу, посмотрите результат и перейдите к нужному инструменту Canva.
          </p>
          <div className="button-row">
            <ButtonLink size="lg">Попробовать бесплатно</ButtonLink>
          </div>
        </div>
        <figure className="hero-media">
          <img
            src="/assets/hero-creative-flow.webp"
            width="1586"
            height="992"
            alt="Процесс создания презентации, поста и фотодизайна"
            fetchPriority="high"
          />
        </figure>
      </div>
    </section>
  )
}

function Capabilities() {
  return (
    <section className="section capability-section" id="capabilities">
      <div className="container">
        <div className="section-heading narrow">
          <h2>Что хотите сделать?</h2>
          <p>Начните не с выбора функции, а с результата, который нужен прямо сейчас.</p>
        </div>
        <div className="capability-grid">
          <article className="cap-card cap-card-large cap-card-cobalt">
            <div className="cap-card-content">
              <span className="card-kicker">Презентации</span>
              <h3>Соберите слайды из идеи</h3>
              <p>Задайте тему, получите структуру и доработайте каждый экран.</p>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/nejroset-dlya-prezentacij-visual-01.webp"
              width="1536"
              height="1024"
              alt="Рабочее место с презентацией на ноутбуке и распечатанными слайдами"
              loading="lazy"
              decoding="async"
            />
          </article>
          <article className="cap-card cap-card-tall">
            <div className="cap-card-content">
              <span className="card-kicker">Изображения</span>
              <h3>Создайте картинку по описанию</h3>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/generator-izobrazhenij-visual-01.webp"
              width="1536"
              height="1024"
              alt="Иллюстрация, созданная из идеи, на экране ноутбука"
              loading="lazy"
              decoding="async"
            />
            <div className="prompt-preview">
              <span>Современный интерьер в утреннем свете</span>
              <b>Создать</b>
            </div>
          </article>
          <article className="cap-card cap-card-soft">
            <div className="cap-card-content">
              <span className="card-kicker">Фото</span>
              <h3>Удалите или замените фон</h3>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/udalit-fon-visual-01.webp"
              width="1536"
              height="1024"
              alt="Ноутбук до и после замены окружающего фона"
              loading="lazy"
              decoding="async"
            />
          </article>
          <article className="cap-card cap-card-wide">
            <div className="cap-card-content">
              <span className="card-kicker">Редактор</span>
              <h3>Обработайте фото в одном месте</h3>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/redaktor-foto-visual-01.webp"
              width="1536"
              height="1024"
              alt="Цветные фотографии в редакторе на экране ноутбука"
              loading="lazy"
              decoding="async"
            />
          </article>
          <article className="cap-card cap-card-coral">
            <div className="cap-card-content">
              <span className="card-kicker">Объекты</span>
              <h3>Уберите лишнее из кадра</h3>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/dobavit-udalit-obekt-na-foto-visual-01.webp"
              width="1536"
              height="1024"
              alt="Сравнение фотографии до и после удаления человека из кадра"
              loading="lazy"
              decoding="async"
            />
          </article>
          <article className="cap-card cap-card-yellow">
            <div className="cap-card-content">
              <span className="card-kicker">Коллаж</span>
              <h3>Объедините несколько фото</h3>
            </div>
            <img
              className="cap-card-photo"
              src="/assets/generated/obedinit-foto-visual-01.webp"
              width="1536"
              height="1024"
              alt="Коллаж из фотографий процесса создания дизайна"
              loading="lazy"
              decoding="async"
            />
          </article>
        </div>
      </div>
    </section>
  )
}

function ProductExperience() {
  const [activeTask, setActiveTask] = useState(0)
  const task = tasks[activeTask]
  return (
    <section className="section experience-section" id="experience">
      <div className="container experience-grid">
        <div className="section-heading">
          <p className="eyebrow">Попробуйте сценарий</p>
          <h2>От задачи к готовому результату</h2>
          <p>
            Выберите формат и получите понятную точку старта. Всё важное остаётся редактируемым.
          </p>
          <div className="task-list" aria-label="Выбор сценария">
            {tasks.map((item, index) => (
              <a
                className={`task-button${activeTask === index ? ' active' : ''}`}
                href="/go/"
                onMouseEnter={() => setActiveTask(index)}
                onFocus={() => setActiveTask(index)}
                aria-current={activeTask === index ? 'true' : undefined}
                key={item.label}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                {item.label}
              </a>
            ))}
          </div>
        </div>
        <div className="experience-panel" aria-live="polite">
          <div className="experience-toolbar">
            <span className="toolbar-title">{task.toolbar}</span>
            <a href="/go/">Поделиться</a>
          </div>
          <div className="experience-body" key={activeTask}>
            <aside className="slide-rail" aria-label="Слайды">
              <span className="slide-thumb selected">1</span>
              <span className="slide-thumb">2</span>
              <span className="slide-thumb">3</span>
            </aside>
            <div className="slide-canvas">
              <span className="slide-label">{task.eyebrow}</span>
              <strong>{task.title}</strong>
              <div className="slide-art" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ResultsGallery() {
  return (
    <section className="section showcase-section" id="showcase">
      <div className="container">
        <div className="section-heading narrow">
          <h2>Один редактор, разные форматы</h2>
          <p>
            Пост, афиша, презентация или карточка товара. Показываем результат, а не список функций.
          </p>
        </div>
        <figure className="gallery-media">
          <img
            src="/assets/results-gallery.webp"
            width="1536"
            height="1024"
            alt="Галерея из шести разных дизайнерских результатов"
            loading="lazy"
            decoding="async"
          />
        </figure>
      </div>
    </section>
  )
}

function Steps() {
  return (
    <section className="section steps-section">
      <div className="container">
        <div className="section-heading narrow">
          <h2>Три действия до первого макета</h2>
        </div>
        <ol className="steps-flow">
          <li>
            <span>01</span>
            <h3>Выберите задачу</h3>
            <p>Презентация, фото, пост или новый визуал.</p>
          </li>
          <li>
            <span>02</span>
            <h3>Добавьте материал</h3>
            <p>Опишите идею или загрузите готовый файл.</p>
          </li>
          <li>
            <span>03</span>
            <h3>Доработайте результат</h3>
            <p>Настройте композицию, текст и детали.</p>
          </li>
        </ol>
      </div>
    </section>
  )
}

function PresentationWorkflow() {
  return (
    <section className="section presentation-section">
      <div className="container presentation-grid">
        <div className="presentation-copy">
          <p className="eyebrow">Презентации</p>
          <h2>Из темы в структуру слайдов</h2>
          <p>
            Сначала получите логику рассказа, затем выберите визуальный ритм и адаптируйте
            содержание под аудиторию.
          </p>
          <a className="text-link" href="/go/">
            Создать презентацию <span aria-hidden="true">→</span>
          </a>
        </div>
        <div className="presentation-visual" aria-label="Пример превращения плана в готовый слайд">
          <div className="deck-card deck-card-back">
            <div className="deck-stage-heading">
              <span>01</span>
              <strong>План</strong>
            </div>
            <div className="deck-outline" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </div>
          <div className="deck-card deck-card-middle">
            <div className="deck-stage-heading">
              <span>02</span>
              <strong>Структура</strong>
            </div>
            <div className="deck-layout" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
          <div className="deck-card deck-card-front">
            <span className="deck-badge">Готовый слайд</span>
            <strong>Идея становится понятной</strong>
            <div className="deck-meta">
              <span>Главная мысль</span>
              <span>03 / 08</span>
            </div>
            <div className="deck-chart" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function BeforeAfter() {
  return (
    <section className="section before-after-section">
      <div className="container">
        <div className="section-heading narrow">
          <h2>Меняйте фон, сохраняя главное</h2>
          <p>Объект остаётся на месте. Меняется только окружение и настроение кадра.</p>
        </div>
        <figure className="before-after-media">
          <img
            src="/assets/before-after-background.webp"
            width="1672"
            height="941"
            alt="Одинаковые кресло и растение до и после замены фона"
            loading="lazy"
            decoding="async"
          />
          <figcaption>
            <span>Исходное фото</span>
            <span>Новый фон</span>
          </figcaption>
        </figure>
      </div>
    </section>
  )
}

function PhotoEditor() {
  const [tool, setTool] = useState('Улучшить')
  const [values, setValues] = useState({ brightness: 54, contrast: 38, saturation: 62 })
  const tools = ['Улучшить', 'Обрезать', 'Фон', 'Объекты', 'Текст']
  const controls = [
    ['Яркость', 'brightness'],
    ['Контраст', 'contrast'],
    ['Насыщенность', 'saturation'],
  ] as const
  return (
    <section className="section editor-section" id="tools">
      <div className="container editor-grid">
        <div className="editor-panel">
          <div className="editor-menu" aria-label="Инструменты редактора">
            {tools.map((item) => (
              <a
                href="/go/"
                className={tool === item ? 'active' : ''}
                onMouseEnter={() => setTool(item)}
                onFocus={() => setTool(item)}
                aria-current={tool === item ? 'true' : undefined}
                key={item}
              >
                {item}
              </a>
            ))}
          </div>
          <div className="editor-preview">
            <div
              className="editor-photo"
              aria-label={`Предпросмотр: ${tool.toLowerCase()}`}
              role="img"
            >
              <div className="editor-status">
                <span>Новый визуал</span>
                <b>{tool}</b>
              </div>
              <div className="editor-artboard">
                <div className="editor-artboard-copy">
                  <small>Коллекция · 2026</small>
                  <strong>Тихий интерьер</strong>
                  <span>Форма, свет и спокойные оттенки</span>
                </div>
                <div className="editor-artwork" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </div>
                <div className="editor-palette" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </div>
                <div className="editor-selection" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>
            <div className="control-card">
              {controls.map(([label, key]) => (
                <div className="control-row" key={key}>
                  <label htmlFor={`editor-${key}`}>
                    {label} <span>{values[key]}</span>
                  </label>
                  <input
                    id={`editor-${key}`}
                    aria-label={label}
                    type="range"
                    min="0"
                    max="100"
                    value={values[key]}
                    onChange={(event) =>
                      setValues({ ...values, [key]: Number(event.target.value) })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="editor-copy">
          <h2>Фото готово к публикации</h2>
          <p>
            Кадрируйте, улучшайте, удаляйте объекты и добавляйте текст без перехода между
            несколькими программами.
          </p>
          <ButtonLink>Редактировать фото</ButtonLink>
        </div>
      </div>
    </section>
  )
}

function UseCases() {
  return (
    <section className="section usecase-section">
      <div className="container">
        <div className="section-heading narrow">
          <h2>Дизайн под конкретную задачу</h2>
        </div>
        <div className="usecase-grid">
          <article className="usecase-main">
            <span>Для работы</span>
            <h3>Презентация продукта</h3>
            <p>Покажите ценность и следующий шаг без перегруженных слайдов.</p>
            <div className="usecase-art usecase-art-deck" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </article>
          <article className="usecase-blue">
            <span>Для соцсетей</span>
            <h3>Пост и история</h3>
            <div className="usecase-art usecase-art-social" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </article>
          <article className="usecase-photo">
            <span>Для продаж</span>
            <h3>Карточка товара</h3>
            <div className="usecase-art usecase-art-product" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </article>
          <article className="usecase-paper">
            <span>Для события</span>
            <h3>Афиша и приглашение</h3>
            <div className="usecase-art usecase-art-paper" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </article>
          <article className="usecase-list">
            <span>Ещё форматы</span>
            <p>Резюме, обложка, баннер, документ и рекламный креатив.</p>
            <div className="usecase-art usecase-art-formats" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </article>
        </div>
      </div>
    </section>
  )
}

function SharedWorkflow() {
  return (
    <section className="section workflow-section">
      <div className="container workflow-grid">
        <div className="section-heading">
          <h2>От идеи к общей версии</h2>
          <p>
            Соберите черновик, уточните детали и передайте готовый материал без путаницы в файлах.
          </p>
        </div>
        <div className="workflow-line" aria-label="Рабочий процесс">
          <article>
            <span>Идея</span>
            <strong>Опишите задачу</strong>
          </article>
          <article>
            <span>Черновик</span>
            <strong>Выберите вариант</strong>
          </article>
          <article>
            <span>Правки</span>
            <strong>Уточните детали</strong>
          </article>
          <article>
            <span>Готово</span>
            <strong>Экспортируйте результат</strong>
          </article>
        </div>
      </div>
    </section>
  )
}

function PracticalValue() {
  return (
    <section className="section value-section">
      <div className="container value-grid">
        <h2>Почему путь начинается с задачи</h2>
        <div className="value-list">
          <article>
            <span>01</span>
            <div>
              <h3>Понятный первый шаг</h3>
              <p>Не нужно изучать весь редактор до начала работы.</p>
            </div>
          </article>
          <article>
            <span>02</span>
            <div>
              <h3>Результат виден заранее</h3>
              <p>Каждая страница показывает действие и ожидаемый формат.</p>
            </div>
          </article>
          <article>
            <span>03</span>
            <div>
              <h3>Связанные инструменты рядом</h3>
              <p>После одной операции легко перейти к следующей.</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  )
}

function Disclosure() {
  return (
    <section className="section disclosure-section">
      <div className="container disclosure-panel">
        <div>
          <span className="card-kicker">Важно</span>
          <h2>Независимый проект о возможностях Canva</h2>
        </div>
        <p>
          Мы не выдаём этот сайт за официальный ресурс и не обещаем функции, тарифы или доступность
          без проверки. Перед началом работы условия нужно уточнить на официальном сайте Canva.
        </p>
      </div>
    </section>
  )
}

function Faq() {
  return (
    <section className="section faq-section" id="faq">
      <div className="container faq-grid">
        <div className="section-heading">
          <h2>Частые вопросы</h2>
          <p>Коротко о том, как устроен этот независимый путеводитель.</p>
        </div>
        <div className="faq-list">
          {homeContent.faq.map((item, index) => (
            <details open={index === 0} key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="final-section" id="start">
      <div className="container final-panel">
        <div className="final-copy">
          <h2>Начните с нужного результата</h2>
          <p>Выберите формат, посмотрите сценарий и перейдите к подходящему инструменту.</p>
          <ButtonLink size="lg">Попробовать бесплатно</ButtonLink>
        </div>
        <div className="final-orbit" aria-hidden="true">
          <span className="orbit-card orbit-one" />
          <span className="orbit-card orbit-two" />
          <span className="orbit-card orbit-three" />
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="site-footer" id="site-footer">
      <div className="container footer-grid">
        {footerGroups.map((group) => (
          <div key={group.title}>
            <h3>{group.title}</h3>
            {group.links.map(([label, href]) => (
              <a href={href} key={href}>
                {label}
              </a>
            ))}
          </div>
        ))}
      </div>
      <div className="container footer-copyright">
        CANVA ©
        <noscript>
          <div>
            <img
              src="https://mc.yandex.ru/watch/112031631"
              className="metrika-pixel"
              width="1"
              height="1"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </div>
        </noscript>
      </div>
    </footer>
  )
}

function NotFound() {
  return (
    <main className="not-found" id="main-content">
      <div className="container">
        <p className="eyebrow">Ошибка 404</p>
        <h1>Такой страницы нет</h1>
        <p>Адрес мог измениться. Вернитесь на главную или начните новый дизайн.</p>
        <div className="button-row">
          <a className="button button-secondary button-lg" href="/">
            На главную
          </a>
          <ButtonLink size="lg">Попробовать бесплатно</ButtonLink>
        </div>
      </div>
    </main>
  )
}

function App() {
  usePageMotion()
  const normalizedPath =
    window.location.pathname === '/' ? '/' : `${window.location.pathname.replace(/\/+$/, '')}/`
  const page = pages.find((item) => item.route === normalizedPath)
  const isHome = window.location.pathname === '/'
  return (
    <>
      <a className="skip-link" href="#main-content">
        Перейти к содержанию
      </a>
      <Header />
      {isHome ? (
        <main id="main-content">
          <Hero />
          <Capabilities />
          <ProductExperience />
          <ResultsGallery />
          <Steps />
          <PresentationWorkflow />
          <BeforeAfter />
          <PhotoEditor />
          <UseCases />
          <SharedWorkflow />
          <PracticalValue />
          <Disclosure />
          <Faq />
          <FinalCta />
        </main>
      ) : page ? (
        <SeoPage page={page} />
      ) : (
        <NotFound />
      )}
      <Footer />
    </>
  )
}

export default App
